<template lang="pug">
.tegaki-canvas(ref='canvasArea')
  canvas-drawing-base.tegaki-canvas__canvas(
    @dragStart='dragStart',
    @dragEnd='dragEnd',
    @dragging='dragging',
    ref='canvas'
  )
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch, Ref } from 'vue-property-decorator'
import CanvasDrawingBase from '@/components/atoms/CanvasDrawingBase.vue'
import { DrawSettings } from '@/models/drawSettings'
import { PenSettings } from '@/const/PenSettings'
import { StrokePoint } from '@/models/StrokePoint'
import { CanvasUtils } from '@/utils/CanvasUtils'
import { throttle, debounce } from 'lodash'

/**
 * 手書きダイアログ用キャンバス
 * 文字列変換に使用する座標情報をemitする
 */
@Component({
  components: {
    CanvasDrawingBase,
  },
})
export default class TegakiCanvas extends Vue {
  // 描画モード
  @Prop({ default: 'pen' })
  mode!: DrawSettings.TegakiMode
  // ストローク座標情報
  @Prop()
  strokes!: StrokePoint[][]

  // canvas表示エリアアクセス用
  @Ref()
  public canvasArea!: HTMLElement
  // canvasコンポーネントアクセス用
  @Ref()
  canvas!: CanvasDrawingBase

  // 描画中フラグ
  private isActive = false
  // 現在描画中のストローク座標情報
  private currentStroke: StrokePoint[] = []

  private debouncedHandleResize = debounce(throttle(this.adjustAndDraw, 500), 300)

  @Watch('strokes', { deep: true })
  private onStrokes() {
    this.draw()
  }

  @Watch('currentStroke')
  private onCurrentStroke() {
    this.draw()
  }

  private mounted() {
    // ウィンドウサイズ変更を監視
    window.addEventListener('resize', this.debouncedHandleResize)

    // キャンバスサイズを設定
    this.waitForCanvasSize()
  }

  /**
   * レンダリングの後にcanvasのサイズを設定
   */
  private waitForCanvasSize() {
    // canvasAreaが連結されているかを確認
    if (!this.canvasArea) {
      requestAnimationFrame(() => this.waitForCanvasSize())
      return
    }

    const width = this.canvasArea.offsetWidth
    const height = this.canvasArea.offsetHeight

    if (width === 0 || height === 0) {
      requestAnimationFrame(() => this.waitForCanvasSize())
    } else {
      this.adjustCanvasSize()
    }
  }

  private beforeDestroy() {
    // イベントの削除
    window.removeEventListener('resize', this.debouncedHandleResize)
  }

  /**
   * キャンバスに描画
   * 都度キャンバス内容をクリア後、ストローク座標情報を用いて描画する
   */
  private draw() {
    if (!this.canvas.context) return
    this.canvas.context.beginPath()

    // キャンバス内容クリア
    this.canvas.context.clearRect(0, 0, this.canvas.canvasElement.width, this.canvas.canvasElement.height)

    // これまでのストローク情報に今回のストローク情報を追加
    const strokes = this.strokes.concat([this.currentStroke])
    strokes.forEach((strokePoints, i) => {
      if (this.mode === 'eraser' && i === strokes.length - 1) {
        // 消しゴムモードかつ、ストローク情報末尾に追加した今回のストロークを描画する時は消しゴム用の設定を使用
        this.setPenSetting(PenSettings.TEGAKI_ERASER)
      } else {
        // それ以外はペン用の設定を使用する
        this.setPenSetting(PenSettings.TEGAKI_PEN)
      }
      this.drawStroke(strokePoints)
    })
  }

  private setPenSetting(penSetting: DrawSettings.TegakiPenAsset) {
    this.canvas.context.lineWidth = penSetting.lineWidth
    this.canvas.context.lineCap = penSetting.lineCap
    this.canvas.context.lineJoin = penSetting.lineJoin
    this.canvas.context.strokeStyle = penSetting.color
  }

  private drawStroke(strokePoints: StrokePoint[]) {
    if (!this.canvas.context) return
    this.canvas.context.beginPath()

    strokePoints.forEach((strokePoint, i) => {
      if (i === 0) {
        // ストロークの開始地点へはmoveTo
        this.canvas.context.moveTo(strokePoint.x, strokePoint.y)
      } else {
        // それ以外はlineTo
        this.canvas.context.lineTo(strokePoint.x, strokePoint.y)
      }
    })

    this.canvas.context.stroke()
  }

  /**
   * キャンバスサイズの調整が必要な場合は調整する
   */
  private adjustCanvasSize() {
    if (CanvasUtils.canvasSizeMismatch(this.canvasArea, this.canvas.canvasElement)) {
      this.canvas.setCanvasSize(this.canvasArea.offsetWidth, this.canvasArea.offsetHeight)
    }
  }

  /**
   * ウィンドウサイズ変更検知時、サイズを計算し再描画する
   */
  public adjustAndDraw() {
    this.adjustCanvasSize()
    this.draw()
  }

  /**
   * 手書きイベント
   * ドラッグ開始時とドラッグ中
   *   現在の座標をcurrentStrokeに追加
   * ドラッグ終了時
   *   strokesとcurrentStrokeによって算出された座標群をemitする
   */
  private dragStart(e: TouchEvent | MouseEvent) {
    // スクロール抑制
    e.preventDefault()

    if (this.isActive) return

    this.isActive = true
    this.currentStroke.push(CanvasUtils.caliculatePosition(e, this.canvas.canvasElement))
  }

  private dragging(e: TouchEvent | MouseEvent) {
    // スクロール抑制
    e.preventDefault()

    if (!this.isActive) return

    this.currentStroke.push(CanvasUtils.caliculatePosition(e, this.canvas.canvasElement))
  }

  private dragEnd(e: TouchEvent | MouseEvent) {
    // スクロール抑制
    e.preventDefault()

    if (!this.isActive) return

    this.isActive = false

    // ストローク情報算出
    const strokes = this.calculateStrokes()
    this.currentStroke = []
    this.$emit('input', strokes)
  }

  /**
   * ドラッグ終了時に返すストローク座標情報を算出する
   */
  private calculateStrokes(): StrokePoint[][] {
    switch (this.mode) {
      case 'pen': {
        // ペンモードはこれまでのストローク情報に今回のストローク情報を追加
        return this.strokes.concat([this.currentStroke])
      }
      case 'eraser': {
        // 消しゴムモードはこれまでのストローク情報から今回のストロークと交差するものを取り除く
        const strokes: StrokePoint[][] = []
        this.strokes.forEach((strokePoints) => {
          let isIntersect = false

          // これまでのストロークと今回のストロークが交差するかをチェック
          for (let i = 1; i < strokePoints.length; i++) {
            const xmn1 = Math.min(strokePoints[i].x, strokePoints[i - 1].x)
            const xmx1 = Math.max(strokePoints[i].x, strokePoints[i - 1].x)
            const ymn1 = Math.min(strokePoints[i].y, strokePoints[i - 1].y)
            const ymx1 = Math.max(strokePoints[i].y, strokePoints[i - 1].y)

            for (let j = 1; j < this.currentStroke.length; j++) {
              const xmn2 = Math.min(this.currentStroke[j].x, this.currentStroke[j - 1].x)
              const xmx2 = Math.max(this.currentStroke[j].x, this.currentStroke[j - 1].x)
              const ymn2 = Math.min(this.currentStroke[j].y, this.currentStroke[j - 1].y)
              const ymx2 = Math.max(this.currentStroke[j].y, this.currentStroke[j - 1].y)

              if (xmn1 <= xmx2 && ymn1 <= ymx2 && xmn2 <= xmx1 && ymn2 <= ymx1) {
                isIntersect = true
              }
            }
          }

          // 交差しないストローク情報のみを返却対象とする
          if (!isIntersect) strokes.push(strokePoints)
        })
        return strokes
      }
    }
  }
}
</script>

<style lang="stylus" scoped>
.tegaki-canvas
  pointer-events none

  &__canvas
    pointer-events auto
</style>
